import React from "react";
import PropTypes from "prop-types";
import { RCL as useTranslation } from "../../RCL";
import { PageTabs } from "./PageTabs";
import { UtilityFuncs } from "../../../utils/PageTabLogic/utilityFuncs";
import { validateChildContent } from "../../../utils/PageTabLogic/validateChildContent";
import { cloudinaryGenURL } from "../../../utils/cloudinaryHelpers";
import { extractTransformationAndPublicIdSegment } from "../utils/extractTransformationAndPublicIDSegment";

const PageTabsConnector = ({ hotelData }) => {
	const translations = {
		elevators: useTranslation({ searchKey: "elevators" }),
		floors: useTranslation({ searchKey: "floor-s" }),
		propertyAmenities: useTranslation({ searchKey: "property-amenities" }),
		ages: useTranslation({ searchKey: "ages" }),
		hoursOfOperation: useTranslation({ searchKey: "hours-of-operation" }),
		note: useTranslation({ searchKey: "note" }),

		hotelLocation: useTranslation({ searchKey: "hotel-location" }),
		hotelInfo: useTranslation({ searchKey: "hotel-information" }),
		airportInfo: useTranslation({ searchKey: "airport-information" }),
		collections: useTranslation({ searchKey: "hotel-collections" }),
		hotelAndAirportInfo: useTranslation({ searchKey: "hotelandairportinfo" }),
	};

	// parse description tab data
	const completeDescriptionTabs = [];

	// Resort overview
	const overviewDescription = hotelData.ResortOverview?.Description;

	if (overviewDescription) {
		const overviewObj = {
			id: "overview",
			order: hotelData.ResortOverview?.Order,
			cardText: hotelData.ResortOverview?.Name,
			description: overviewDescription,
			icon: "cruise-overview",
			cardKey: "overview",
		};

		if (validateChildContent(overviewObj)) {
			completeDescriptionTabs.push(overviewObj);
		}
	}

	// Meal plan
	const mealPlans = hotelData.MealPlanFeatures?.MealPlan;

	if (mealPlans?.length) {
		const mealPlansDescription = (
			<div>
				{mealPlans.map((mealPlan, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<React.Fragment key={index}>
						{!!mealPlan?.Name && <p>{mealPlan.Name}</p>}
						<div
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: mealPlan?.Description,
							}}
						/>
					</React.Fragment>
				))}
			</div>
		);
		const mealPlanObj = {
			id: "meal-plans",
			order: hotelData.MealPlanFeatures?.Order,
			cardText: hotelData.MealPlanFeatures?.Name,
			description: mealPlansDescription,
			icon: "meal",
			cardKey: "meals",
		};

		if (validateChildContent(mealPlanObj)) {
			completeDescriptionTabs.push(mealPlanObj);
		}
	}

	// Property Amenities
	const amenities = [
		{
			label: hotelData.ResortOverview?.BuildingsTitle,
			value: hotelData.ResortOverview?.NumberOfBuildings,
		},
		{
			label: translations.elevators,
			value: hotelData.ResortOverview?.NumberOfElevators,
		},
		{
			label: hotelData.ResortOverview?.RoomsTitle,
			value: hotelData.ResortOverview?.NumberOfRooms,
		},
		{
			label: hotelData.ResortOverview?.PoolsTitle,
			value: hotelData.ResortOverview?.NumberOfPools,
		},
		{
			label: translations.floors,
			value: hotelData?.ResortOverview?.NumberOfFloors,
		},
		{
			label: hotelData.DiningAndEntertainment?.RestaurantsTitle,
			value: hotelData.DiningAndEntertainment?.Restaurant?.length,
		},
		{
			label: hotelData.DiningAndEntertainment?.BarsTitle,
			value: hotelData.DiningAndEntertainment?.Bar?.length,
		},
	].filter(amenity => !!amenity.value);

	if (amenities?.length) {
		const amenitiesList = (
			<ul>
				{amenities.map((amenity, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<li key={index}>{`${amenity.value} ${amenity.label}`}</li>
				))}
			</ul>
		);
		const amenitiesTab = {
			id: "amenities",
			order: 3,
			cardText: translations.propertyAmenities,
			description: amenitiesList,
			icon: "hotel--o",
			cardKey: "amenities",
		};

		if (validateChildContent(amenitiesTab)) {
			completeDescriptionTabs.push(amenitiesTab);
		}
	}

	// Accommodations
	if (hotelData.Accomodations?.RoomCategory?.length) {
		const basicRoomAmenities = hotelData.Accomodations?.BasicRoomAmenities;
		const rooms = hotelData.Accomodations.RoomCategory.map(room => ({
			title: room?.Name,
			maxOccupancy: room?.MaximumOccupancy,
			maxNumAdults: room?.MaximumNumberOfAdults,
			maximumChildren: room?.MaximumNumberOfChildren,
			roomSize: room?.RoomSize,
			roomAmenities: [...basicRoomAmenities, ...room.Amenities],
			images: room?.images?.map((img, index) => {
				const { transformations, publicId, baseUrl } = extractTransformationAndPublicIdSegment(
					img?.src
				);

				return {
					alt: "",
					id: index,
					public_id: publicId,
					source: cloudinaryGenURL({
						aspectRatio: 16 / 9,
						transformations,
						baseUrl,
						cloudinaryImage: {
							public_id: publicId,
							width: 800,
						},
					})?.data,
				};
			}),
		}));

		const accommodationsTab = {
			id: "accommodations",
			order: hotelData.Accomodations?.Order,
			cardText: hotelData.Accomodations?.Name,
			rooms,
			icon: "accommodations",
			cardKey: "accommodations",
		};

		if (accommodationsTab.rooms?.length) {
			if (validateChildContent(accommodationsTab)) {
				completeDescriptionTabs.push(accommodationsTab);
			}
		}
	}

	// Dining and entertainment
	const restaurantLocations = hotelData.DiningAndEntertainment?.Restaurant;
	const barLocations = hotelData.DiningAndEntertainment?.Bar;

	const parsedDiningLocations = [];

	if (restaurantLocations || barLocations) {
		if (restaurantLocations?.length) {
			parsedDiningLocations.push({
				category: hotelData.DiningAndEntertainment?.RestaurantsTitle,
				locations: restaurantLocations.map(restaurant => ({
					title: restaurant.Name,
					location: restaurant.Location?.join(", "),
					style: restaurant.Style?.join(", "),
					cuisine: restaurant.Cuisine?.join(", "),
					operationHours: UtilityFuncs.operationHour(restaurant, translations),
					amenities: restaurant.Amenities,
					additionalComments: restaurant.AdditionalComments,
				})),
			});
		}

		if (barLocations?.length) {
			parsedDiningLocations.push({
				category: hotelData.DiningAndEntertainment?.BarsTitle,
				locations: barLocations.map(bar => ({
					title: bar.Name,
					location: bar.Location?.join(", "),
					operationHours: UtilityFuncs.operationHour(bar, translations),
					amenities: bar.Amenities,
					additionalComments: bar.AdditionalComments,
				})),
			});
		}
		const diningAndEntertainmentObj = {
			id: "dining-and-entertainment",
			order: hotelData.DiningAndEntertainment?.Order,
			cardText: hotelData.DiningAndEntertainment?.Name,
			dining: parsedDiningLocations,
			icon: "entertainment",
			cardKey: "entertainment",
		};

		if (validateChildContent(diningAndEntertainmentObj)) {
			completeDescriptionTabs.push(diningAndEntertainmentObj);
		}
	}

	// Extra features
	const featureArray = [];
	const bonusFeatures = hotelData.SpecialFeatures?.BonusFeatures;
	const extraFeatures = hotelData.SpecialFeatures?.ExtraFeatures;

	if (bonusFeatures?.length) {
		featureArray.push({
			title: hotelData.SpecialFeatures?.BonusFeaturesTitle,
			panels: bonusFeatures
				.filter(x => x?.Comments && x?.Description)
				.map((feature, index) => ({
					key: `bonus-feature-panel-${index}`,
					title: feature.Description,
					content: feature.Comments,
				})),
		});
	}

	if (extraFeatures?.length) {
		featureArray.push({
			title: hotelData.SpecialFeatures?.ExtraFeaturesTitle,
			panels: extraFeatures
				.filter(x => x?.Comments && x?.Description)
				.map((feature, index) => ({
					key: `extra-feature-panel-${index}`,
					title: feature.Description,
					content: feature.Comments,
				})),
		});
	}

	if (featureArray.length) {
		const featureAccordionsObj = {
			id: "special-features",
			order: hotelData.SpecialFeatures?.Order,
			cardText: hotelData.SpecialFeatures?.Name,
			features: featureArray,
			icon: "favotite--c",
			cardKey: "features",
		};

		if (validateChildContent(featureAccordionsObj)) {
			completeDescriptionTabs.push(featureAccordionsObj);
		}
	}

	// Facilities and services
	const facilitiesArray = [];
	const facilitiesData = hotelData.FacilitiesAndServices || {};

	// Internet Services
	const internetServices = hotelData.Wifi?.InternetServices?.filter(
		service => service?.WiFiPolicy
	)?.map(service => service.WiFiPolicy);

	if (internetServices?.length) {
		facilitiesArray.push({
			key: "facilities--wifi",
			title: hotelData.Wifi?.Name,
			features: internetServices,
		});
	}

	// Facilities and services - Beach
	if (facilitiesData.Beach) {
		facilitiesArray.push({
			key: "facilities--beach",
			title: facilitiesData.BeachTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.Beach),
		});
	}

	// Facilities and services - Parking
	if (facilitiesData.Parking) {
		facilitiesArray.push({
			key: "facilities--parking",
			title: facilitiesData.ParkingTitle,
			features: facilitiesData.Parking.filter(x => x),
		});
	}

	// Facilities and services - Misc. resort amenities / services
	if (facilitiesData.MiscellaneousResortAmenitiesService) {
		facilitiesArray.push({
			key: "facilities--misc-resort-amenities-services",
			title: facilitiesData.MiscellaneousResortAmenitiesServiceTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.MiscellaneousResortAmenitiesService),
		});
	}

	// Facilities and services - Business services
	if (facilitiesData.BusinessServices) {
		facilitiesArray.push({
			key: "facilities--business-services",
			title: facilitiesData.BusinessServicesTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.BusinessServices),
		});
	}

	// Facilities and services - Children
	if (facilitiesData.Children) {
		facilitiesArray.push({
			key: "facilities--children",
			title: facilitiesData.ChildrenTitle,
			features: facilitiesData.Children?.map(x => {
				const output = [x.FeatureTitle];

				if (x.MinimumAge && x.MaximumAge) {
					output.push(
						<>
							<br />
							<span>
								{translations.ages}: {x.MinimumAge} - {x.MaximumAge}
							</span>
						</>
					);
				}

				if (x.OpeningTime && x.ClosingTime) {
					output.push(
						<>
							<br />
							<span>
								{translations.hoursOfOperation}: {x.OpeningTime} - {x.ClosingTime}
							</span>
						</>
					);
				}

				if (x.AdditionalComments) {
					output.push(
						<div>
							{translations.note}
							{": "}
							{/* eslint-disable-next-line react/no-danger */}
							<span dangerouslySetInnerHTML={{ __html: x.AdditionalComments }} />
						</div>
					);
				}

				return output;
			}).filter(x => x),
		});
	}

	// Facilities and services - Group meeting / conference
	if (facilitiesData.GroupMeetingAndConferenceFacilities) {
		facilitiesArray.push({
			key: "facilities--group-meeting-conference",
			title: facilitiesData.GroupMeetingAndConferenceFacilitiesTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.GroupMeetingAndConferenceFacilities),
		});
	}

	// Facilities and services - Golf
	if (facilitiesData.Golf) {
		facilitiesArray.push({
			key: "facilities--golf",
			title: facilitiesData.GolfTitle,
			features: facilitiesData.Golf.filter(x => x),
		});
	}

	// Facilities and services - Health and safety
	if (facilitiesData.HealthAndSafety?.length) {
		facilitiesArray.push({
			key: "facilities--health-and-safety",
			title: facilitiesData.HealthAndSafetyTitle,
			features: facilitiesData.HealthAndSafety.filter(x => x),
		});
	}

	// Facilities and services - Lobby
	if (facilitiesData.HotelLobby?.length) {
		facilitiesArray.push({
			key: "facilities--lobby",
			title: facilitiesData.HotelLobbyTitle,
			features: facilitiesData.HotelLobby?.map(x => {
				if (x.AdditionalComments) {
					return `${x.FeatureTitle} (${x.AdditionalComments})`;
				}

				return x.FeatureTitle;
			}).filter(x => x),
		});
	}

	// Facilities and services - Shops and services
	if (facilitiesData.ShopsAndServices?.length) {
		facilitiesArray.push({
			key: "facilities--shops-services",
			title: facilitiesData.ShopsAndServicesTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.ShopsAndServices),
		});
	}

	// Facilities and services - Sports activities
	if (
		facilitiesData.SportsActivitiesOfferedByHotel?.length ||
		facilitiesData.WaterSportsOfferedByHotel?.length ||
		facilitiesData.Tennis?.length
	) {
		facilitiesArray.push({
			key: "facilities--sports",
			title: facilitiesData.SportsActivitiesOfferedByHotelTitle,
			features: UtilityFuncs.formatFeatures([
				...(facilitiesData.SportsActivitiesOfferedByHotel || []),
				...(facilitiesData.WaterSportsOfferedByHotel || []),
				...(facilitiesData.Tennis || []),
			]),
		});
	}

	// Facilities and services - Wellness / fitness
	if (facilitiesData.Wellness?.length) {
		facilitiesArray.push({
			key: "facilities--wellness",
			title: facilitiesData.WellnessTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.Wellness),
		});
	}

	// Facilities and services - Spa
	if (facilitiesData.Spa?.length) {
		facilitiesArray.push({
			key: "facilities--spa",
			title: facilitiesData.SpaTitle,
			features: UtilityFuncs.formatFeatures(facilitiesData.Spa),
		});
	}

	// Facilities and services - Pools
	if (facilitiesData.SwimmingPools?.length || facilitiesData.ChildrenSPool?.length) {
		facilitiesArray.push({
			key: "facilities--pools",
			title: facilitiesData.SwimmingPoolsTitle,
			features: UtilityFuncs.formatFeatures([
				...(facilitiesData.SwimmingPools || []),
				...(facilitiesData.ChildrenSPool || []),
			]),
		});
	}

	// Update the facilities data with the parsed array
	if (facilitiesArray.length) {
		const facilitiesArrayObj = {
			id: "facilities",
			order: facilitiesData.Order,
			cardText: facilitiesData.Name,
			facilities: facilitiesArray,
			icon: "onboard-facilities",
			cardKey: "facilities",
		};

		if (validateChildContent(facilitiesArrayObj)) {
			completeDescriptionTabs.push(facilitiesArrayObj);
		}
	}

	// Location
	if (hotelData.Location) {
		const policies = hotelData.Policies.Policies.filter(x => x.CheckInTime || x.CheckOutTime);

		const locationTab = {
			id: "location",
			order: hotelData.Location.Order,
			cardText: translations.hotelAndAirportInfo,
			locationDetails: {
				hotelLocation: {
					icon: "pin",
					label: translations.hotelLocation,
					address: hotelData.Location.Address,
					resortMap: hotelData?.HotelImages?.resortMap?.[0],
				},
				airportInfo: {
					icon: "flight-arrival",
					label: translations.airportInfo,
					name: hotelData.Location.AirportName,
					transferTime: hotelData.Location.AirportTransferTime,
				},
				hotelInfo: {
					icon: "destination",
					label: translations.hotelInfo,
					checkInTime: policies?.[0]?.CheckInTime,
					checkOutTime: policies?.[0]?.CheckOutTime,
				},
			},
			cardKey: "location",
		};

		completeDescriptionTabs.push(locationTab);
	}

	// Wedding and romance packages
	const packagesTab = {
		id: "packages",
		order: hotelData.SpecialPackages?.Order,
		cardText: hotelData.SpecialPackages?.Name,
		accordions: [],
		icon: "star",
		cardKey: "packages",
	};

	const specialPackages = hotelData.SpecialPackages || {};

	if (specialPackages.WeddingInformation?.length) {
		packagesTab.accordions.push({
			title: specialPackages.WeddingInformationTitle,
			panels: specialPackages.WeddingInformation.map((wedding, index) => ({
				key: `wedding-${index}`,
				title: wedding.PackageName,
				content: wedding.Description,
				cost: wedding.Cost,
				contact: wedding.ContactEmailAddress,
			})),
		});
	}

	if (specialPackages.HoneymoonInformation?.length) {
		packagesTab.accordions.push({
			title: specialPackages.HoneymoonInformationTitle,
			panels: specialPackages.HoneymoonInformation.map((honeymoon, index) => ({
				key: `honeymoon-${index}`,
				title: honeymoon.PackageName,
				content: honeymoon.Description,
				cost: honeymoon.Cost,
				contact: honeymoon.ContactEmailAddress,
			})),
		});
	}

	if (specialPackages.RenewalOfVowsInformation?.length) {
		packagesTab.accordions.push({
			title: specialPackages.RenewalOfVowsInformationTitle,
			panels: specialPackages.RenewalOfVowsInformation.map((renewalOfVows, index) => ({
				key: `renewalOfVows-${index}`,
				title: renewalOfVows.PackageName,
				content: renewalOfVows.Description,
				cost: renewalOfVows.Cost,
				contact: renewalOfVows.ContactEmailAddress,
			})),
		});
	}

	if (specialPackages.OtherInformation?.length) {
		packagesTab.accordions.push({
			title: specialPackages.OtherInformationTitle,
			panels: specialPackages.OtherInformation.map((other, index) => ({
				key: `other-${index}`,
				title: other.PackageName,
				content: other.Description,
				cost: other.Cost,
				contact: other.ContactEmailAddress,
			})),
		});
	}

	if (validateChildContent(packagesTab)) {
		completeDescriptionTabs.push(packagesTab);
	}

	// Policies
	if (hotelData.Policies?.Policies?.length) {
		const policies = hotelData.Policies.Policies.map(policy => {
			const description = [];

			if (policy.Details) {
				// eslint-disable-next-line react/no-danger
				description.push(<li dangerouslySetInnerHTML={{ __html: policy.Details }} />);
			}

			return description;
		}).filter(arr => arr.length > 0);

		if (policies?.length > 0) {
			const policiesTab = {
				id: "policies",
				order: hotelData.Policies?.Order,
				cardText: hotelData.Policies?.Name,
				description: <ul>{policies}</ul>,
				icon: "policies-vacation-checklist",
				cardKey: "policies",
			};

			if (validateChildContent(policiesTab)) {
				completeDescriptionTabs.push(policiesTab);
			}
		}
	}

	// Alerts
	const alertsTab = {
		id: "alerts",
		order: hotelData.Alerts?.Order,
		cardText: hotelData.Alerts?.Name,
		alerts: hotelData.Alerts?.Descriptions?.map(alert => alert.Description),
		icon: "alert",
		cardKey: "alerts",
	};

	if (validateChildContent(alertsTab)) {
		completeDescriptionTabs.push(alertsTab);
	}

	const collections = hotelData.Collections;

	if (collections) {
		const finalCollections = collections?.map(collection => {
			const copyValues = Object.entries(collection)
				.filter(([key]) => key.startsWith("collectioncopy_"))
				.map(([, value]) => value);

			return {
				...collection,
				collectionDescription: (
					<ul>
						{copyValues.map((copyValue, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<li key={index}>{copyValue}</li>
						))}
					</ul>
				),
			};
		});

		const collectionsAndConceptsTab = {
			id: "collections",
			order: 2,
			cardText: translations.collections,
			collections: finalCollections,
			cardKey: "collections",
		};
		if (collectionsAndConceptsTab.collections?.length) {
			if (validateChildContent(collectionsAndConceptsTab)) {
				completeDescriptionTabs.push(collectionsAndConceptsTab);
			}
		}
	}

	return completeDescriptionTabs ? (
		<PageTabs
			activeKey={completeDescriptionTabs[0]?.cardKey}
			cards={completeDescriptionTabs.sort((a, b) => a.order - b.order)}
		/>
	) : null;
};

PageTabsConnector.propTypes = {
	hotelData: PropTypes.object.isRequired,
};

export default PageTabsConnector;
export { PageTabsConnector };
